import { apiClient } from '@/shared/api/client'
import type { SigningResult, SigningOptions } from '../types'

/**
 * Sign a PDF document with a PKCS#12 digital certificate.
 *
 * @param pdf - PDF file to sign
 * @param certificate - PKCS#12 certificate file (.p12/.pfx)
 * @param password - Certificate password
 * @param options - Optional signer metadata
 * @returns Signing result with signed PDF and verification info
 */
export async function signDocument(
  pdf: File | Blob,
  certificate: File | null,
  password: string,
  options: SigningOptions & {
    signatureId?: string
    shortId?: string
    pdfFilename?: string
    useEphemeralCert?: boolean
    ephemeralSignerEmail?: string
    biometricData?: unknown[]
    signatureImageData?: string
  } = {}
): Promise<SigningResult> {
  const formData = new FormData()
  if (pdf instanceof File) {
    formData.append('pdf', pdf)
  } else {
    formData.append('pdf', pdf, options.pdfFilename || 'document.pdf')
  }
  if (certificate) formData.append('certificate', certificate)
  if (password) formData.append('password', password)
  if (options.useEphemeralCert) {
    formData.append('use_ephemeral_cert', 'true')
    if (options.ephemeralSignerEmail) {
      formData.append('ephemeral_signer_email', options.ephemeralSignerEmail)
    }
  }
  if (options.biometricData && options.biometricData.length > 0) {
    formData.append('biometric_data', JSON.stringify(options.biometricData))
  }
  if (options.signatureImageData) {
    formData.append('signature_image_data', options.signatureImageData)
  }

  if (options.signerName) {
    formData.append('signer_name', options.signerName)
  }
  if (options.signerRole) {
    formData.append('signer_role', options.signerRole)
  }
  if (options.reason) {
    formData.append('reason', options.reason)
  }
  if (options.location) {
    formData.append('location', options.location)
  }
  if (options.signatureId) {
    formData.append('signature_id', options.signatureId)
  }
  if (options.shortId) {
    formData.append('short_id', options.shortId)
  }

  const response = await apiClient.post<SigningResult>(
    '/api/sign-pdf',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60s for signing operations
    }
  )

  return response.data
}
